import { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { selectUserParams } from '../../../store/slices/userParamsSlice';
import { usePermissions } from '../../../hooks/usePermissions';
import { useLanguage } from '../../../contexts/LanguageContext';

import useVorData from './hooks/useVorData';
import TechnicalSupportRequestForm from '../technical-support-request/components/TechnicalSupportRequestForm';
import VorTable from './components/VorTable';
import { useExportVorToExcelMutation } from '../../../api/endpoints/technicalSupportRequest';
import GenericFileExportButton from '../../../components/common/GenericFileExportButton';

const VorPage = () => {
  const { th } = useLanguage();
  const userParams = useSelector(selectUserParams);
  const { hasAdminRole, hasWarrantyTeamRole } = usePermissions();

  const dealerId = useMemo(
    () => userParams?.dealerId ?? userParams?.DealerId ?? null,
    [userParams]
  );

  const isKarsanTeam = hasAdminRole() || hasWarrantyTeamRole();
  const filterDealerId = isKarsanTeam ? null : dealerId;

  const { requests, loading, totalCount, pagination, onPageChange, refetch } = useVorData(filterDealerId);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [mode, setMode] = useState('create');

  const openDialog = useCallback((nextMode, req = null) => {
    setSelectedRequest(req);
    setMode(nextMode);
    setDialogVisible(true);
  }, []);

  const hideDialog = useCallback(() => {
    setDialogVisible(false);
    setSelectedRequest(null);
    setMode('create');
  }, []);

  const handleSaveSuccess = useCallback(() => {
    refetch();
    hideDialog();
  }, [refetch, hideDialog]);

  const handleRefreshRequest = useCallback(() => {
    refetch().then((result) => {
      if (result.data && selectedRequest?.id) {
        const updatedRequest = result.data.items?.find(r => r.id === selectedRequest.id);
        if (updatedRequest) {
          setSelectedRequest(updatedRequest);
        }
      }
    });
  }, [refetch, selectedRequest]);

  return (
    <div className="card">
      <div className="flex justify-content-between align-items-center mb-4">
        <h1 className="m-0">{th('vor.pageTitle')}</h1>

        <div className="flex gap-2">
            <GenericFileExportButton
              useExportQuery={useExportVorToExcelMutation}
              exportParams={{ compressionType: 1 }}
              buttonLabel={th('common.export') || 'Excel'}
              buttonClassName="p-button-success"
              icon="pi pi-file-excel"
              fileNameExtractor={(result) => result?.fileName || 'vor.xlsx'}
              dataExtractor={(result) => result?.fileBase64}
              contentTypeExtractor={(result) => result?.contentType}
            />
                 
            <Button
              label={th('vor.newRecord')}
              icon="pi pi-plus"
              onClick={() => openDialog('create')}
            />
            </div>
      </div>

      <VorTable
        requests={requests}
        loading={loading}
        totalCount={totalCount}
        pagination={pagination}
        onPageChange={onPageChange}
        onEdit={(req) => openDialog('edit', req)}
      />

      <TechnicalSupportRequestForm
        dialogVisible={dialogVisible}
        editMode={mode === 'edit'}
        mode={mode}
        hideDialog={hideDialog}
        request={selectedRequest}
        onSaveSuccess={handleSaveSuccess}
        onRefreshRequest={handleRefreshRequest}
        dealerId={dealerId}
        isVorMode={true}
      />
    </div>
  );
};

export default VorPage;
